<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Clientes" :on-report="() => datatable?.openDialogReport()" />

    <data-table
      ref="datatable"
      :headers="headers"
      :provider="fetch"
      :filters="filters"
      :pagination="pagination"
      :actions="actions"
      :metadata="metadata"
      :report="report"
    >
      <template #[`item.document`]="{ item }">
        <span>{{ formatDocument(item.document, item.document_type) }}</span>
      </template>
      <template #[`item.cellphone`]="{ item }">
        <span>{{ cellphone(item.cellphone) }}</span>
      </template>
      <template #[`item.email`]="{ item }">
        <span>
          <v-tooltip :text="item.email_verified_at ? 'E-mail verificado' : 'E-mail não verificado'">
            <template #activator="{ props }">
              <v-chip v-bind="props" :color="item.email_verified_at === null ? 'red' : 'green'">
                {{ item.email }}
              </v-chip>
            </template>
          </v-tooltip>
        </span>
      </template>
    </data-table>
  </section>
</template>

<script setup lang="ts">
import { Ref, ref, useTemplateRef } from 'vue'
import TPageTitle from '@/components/TPageTitle.vue'
import UserService from '@/services/UserService'
import type {
  DataTableHeader,
  DataTablePagination,
  DataTableFilter,
  DefaultDataTableActions,
  DataTableMeta,
} from '@/types/DataTable'
import DataTable from '@/components/datatable/DataTable.vue'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useAbility } from '@casl/vue'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import { useRouter } from 'vue-router'
import User from '@/types/User'
import AdminService from '@/services/AdminService'
import { useAdminStore } from '@/stores/admin'
import { formatDocument } from '@/helpers/formatters/document'
import { cellphone } from '@/helpers/formatters/cellphone'
import { useDateIntervalFilter } from '@/helpers/reports/dateIntervalFilter'
import { Report } from '@/types/Report'

const { can } = useAbility()
const snackbar = useSnackBar()
const loader = useLoader()
const router = useRouter()

const datatable = useTemplateRef('datatable')

const headers: DataTableHeader[] = [
  { title: 'Nome', value: 'name', key: 'name', sortable: false, align: 'start' },
  { title: 'Documento', value: 'document', key: 'document', sortable: false, align: 'center' },
  { title: 'Celular', value: 'cellphone', key: 'cellphone', sortable: false, align: 'center' },
  { title: 'E-mail', value: 'email', key: 'email', sortable: false, align: 'start' },
  { key: 'actions', value: '', title: 'Ações', sortable: false, align: 'center' },
]

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({ label: 'Nome', field: 'name', required: false }),
  useTextFilter({ label: 'Documento', field: 'document', required: false }),
  useTextFilter({ label: 'E-mail', field: 'email', required: false }),
  useDateIntervalFilter({
    label: 'Data de Cadastro',
    field: 'created_at',
    icon: 'fa-solid fa-clock',
    required: false,
    initial: null,
  }),
])

const actions: DefaultDataTableActions<User> = {
  update: {
    callback: (item: User) => router.push({ name: 'edit-users', params: { id: item.id } }),
    condition: () => can('update', 'users'),
    color: 'info',
  },
  access: {
    callback: (item: User) => login(item),
    condition: () => can('impersonate', 'users'),
    color: 'grey',
  },
}

const metadata: Ref<DataTableMeta> = ref({
  headers: [
    {
      title: 'Cadastros Encontrados',
      value: 'total',
      key: 'total',
      align: 'center',
      sortable: false,
    },
  ],
  items: [],
})

const pagination: Ref<DataTablePagination> = ref({
  page: 1,
  itemsPerPage: 25,
  total: 0,
  totalPages: 0,
})

const report: Ref<Report> = ref({
  items: [
    { label: 'Código', field: 'id', active: true },
    { label: 'Nome', field: 'name', active: true },
    { label: 'Email', field: 'email', active: true },
    { label: 'Documento', field: 'document', active: true },
    { label: 'Telefone', field: 'cellphone', active: true },
    { label: 'Data de Nascimento', field: 'birth_date', active: true },
    { label: 'Data de Cadastro', field: 'created_at', active: true },
  ],
  provider: async (filters: string, selected) => {
    return await new UserService().report(filters, selected)
  },
  filename: 'usuarios_cadastrados',
})

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    const response = await new UserService().index(
      page,
      filters,
      per_page,
    )

    metadata.value.items = [
      { total: response.data.meta?.total ?? 0 },
    ]

    return response
  } catch (error) {
    snackbar.show('Não foi possível buscar os clientes', 'error')

    throw error
  }
}

async function login(item: User) {
  try {
    loader.increment()
    const { data } = await new AdminService().getUserToken(useAdminStore().admin!.numeric_id, item.id)
    const b64_token = btoa(JSON.stringify(data.data.token))
    window.open(`${import.meta.env.VITE_SALES_URL}/login?token=${b64_token}`, '_blank')
  } catch {
    snackbar.show('Erro ao tentar logar como administrador', 'error')
  } finally {
    loader.decrement()
  }
}

</script>
